import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ChatWidget.css';
import { chatbotTokenApi } from 'utils/API/Merchant APIs/MerchantApiCalls';
import { RAG_API_ENDPOINTS } from 'utils/API/RagApis';

const ChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([{ id: 'w1', role: 'bot', text: "Hi! I'm Ace Assistant. How can I help you today?" }]);
    const [value, setValue] = useState('');
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const tokenRef = useRef(null);
    const [quickOptions, setQuickOptions] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [lastAddedMessageId, setLastAddedMessageId] = useState(null);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [historyOffset, setHistoryOffset] = useState(0);
    const [hasMoreHistory, setHasMoreHistory] = useState(true);
    const [tokenReady, setTokenReady] = useState(false);
    const [showFileUpload, setShowFileUpload] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [ticketId, setTicketId] = useState(null);
    const scrollBeforeLoadRef = useRef(null);
    const initialHistoryLoadedRef = useRef(false);
    const agentCode = localStorage.getItem('agentcode');
    const decryptedAgentCode = agentCode;
    const toggle = () => setOpen((p) => !p);

    useEffect(() => {
        if (open && inputRef.current) inputRef.current.focus();
    }, [open]);

    // Scroll behavior: when a new bot message arrives, scroll to its start (not the bottom)
    useEffect(() => {
        if (!listRef.current || !lastAddedMessageId) return;
        const el = listRef.current.querySelector(`[data-mid="${lastAddedMessageId}"]`);
        if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, [lastAddedMessageId]);

    // Ensure typing indicator is visible: when typing starts, scroll to bottom
    useEffect(() => {
        if (!listRef.current) return;
        if (isTyping) {
            try {
                listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
            } catch (_) {
                listRef.current.scrollTop = listRef.current.scrollHeight;
            }
        }
    }, [isTyping]);

    // Fetch auth token once on mount
    useEffect(() => {
        const fetchToken = async () => {
            try {
                const response = await chatbotTokenApi({ username: decryptedAgentCode });
                if (!response.data?.ok) throw new Error('Failed to fetch token');
                const data = response?.data;
                const token = data?.token || data?.access_token || data?.data?.token;
                tokenRef.current = token || null;
                setTokenReady(!!token);
            } catch (err) {
                // Optionally surface an error message in the chat
                setMessages((m) => [
                    ...m,
                    { id: `b-${Date.now()}`, role: 'bot', text: 'Unable to authenticate chat service at the moment.' }
                ]);
            }
        };
        fetchToken();
        // eslint-disable-next-line
    }, []);

    const getAuthHeaders = useCallback(() => {
        const headers = {};
        if (tokenRef.current) headers['Authorization'] = `Bearer ${tokenRef.current}`;
        return headers;
    }, []);

    const fetchHistory = useCallback(
        async (offset = 0, isInitialLoad = false) => {
            if (!tokenRef.current || loadingHistory || (!hasMoreHistory && !isInitialLoad)) return;

            setLoadingHistory(true);
            try {
                const chatUserId = decryptedAgentCode;
                const limit = 20;
                const url = `${RAG_API_ENDPOINTS.history}?chatUserId=${chatUserId}&limit=${limit}&offset=${offset}`;

                const response = await fetch(url, {
                    method: 'GET',
                    headers: getAuthHeaders()
                });
                if (!response.ok) throw new Error('History fetch failed');
                const data = await response.json();

                // Parse history messages from the API response
                const historyMessages = data?.messages || [];

                if (historyMessages.length === 0) {
                    setHasMoreHistory(false);
                    setLoadingHistory(false);
                    return;
                }

                // Transform API messages to our format
                // Each message object contains both userText and botText, so we create separate message objects
                const transformedMessages = [];

                historyMessages.forEach((msg) => {
                    // Add user message if userText exists
                    if (msg.userText) {
                        transformedMessages.push({
                            id: `u-${msg.id}`,
                            role: 'user',
                            text: msg.userText
                        });
                    }
                    // Add bot message if botText exists
                    if (msg.botText) {
                        transformedMessages.push({
                            id: `b-${msg.id}`,
                            role: 'bot',
                            text: msg.botText
                        });
                    }
                });

                if (isInitialLoad) {
                    // For initial load, replace messages with history (keep welcome message if no history)
                    if (transformedMessages.length > 0) {
                        setMessages(transformedMessages);
                        // Scroll to bottom to show latest messages
                        setTimeout(() => {
                            if (listRef.current) {
                                listRef.current.scrollTop = listRef.current.scrollHeight;
                            }
                        }, 100);
                    }
                } else {
                    // For pagination, store scroll position before adding new messages
                    if (listRef.current) {
                        scrollBeforeLoadRef.current = {
                            scrollHeight: listRef.current.scrollHeight,
                            scrollTop: listRef.current.scrollTop
                        };
                    }
                    // Prepend older messages to the beginning
                    setMessages((prev) => [...transformedMessages, ...prev]);
                }

                // Use pagination info from API response
                const pagination = data?.pagination;
                if (pagination) {
                    setHasMoreHistory(pagination.hasMore || false);
                    if (pagination.nextOffset !== undefined) {
                        setHistoryOffset(pagination.nextOffset);
                    } else {
                        setHistoryOffset(offset + limit);
                    }
                } else {
                    // Fallback to old logic if pagination not available
                    if (historyMessages.length < limit) {
                        setHasMoreHistory(false);
                    } else {
                        setHistoryOffset(offset + limit);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch history:', err);
                setHasMoreHistory(false);
            } finally {
                setLoadingHistory(false);
            }
        },
        // eslint-disable-next-line
        [loadingHistory, hasMoreHistory, getAuthHeaders]
    );

    // Restore scroll position after loading history
    useEffect(() => {
        if (!listRef.current || !scrollBeforeLoadRef.current || loadingHistory) return;

        const { scrollHeight, scrollTop } = scrollBeforeLoadRef.current;
        const newScrollHeight = listRef.current.scrollHeight;
        const heightDifference = newScrollHeight - scrollHeight;

        // Restore scroll position adjusted for new content
        listRef.current.scrollTop = scrollTop + heightDifference;
        scrollBeforeLoadRef.current = null;
    }, [messages, loadingHistory]);

    // Fetch initial history when chat opens and token is available
    useEffect(() => {
        if (open && tokenReady && tokenRef.current && !initialHistoryLoadedRef.current) {
            initialHistoryLoadedRef.current = true;
            fetchHistory(0, true);
        }
    }, [open, tokenReady, fetchHistory]);

    // Handle scroll to load history
    useEffect(() => {
        const listEl = listRef.current;
        if (!listEl || !open) return;

        const handleScroll = () => {
            // Check if scrolled near the top (within 100px)
            if (listEl.scrollTop < 100 && hasMoreHistory && !loadingHistory && tokenRef.current) {
                fetchHistory(historyOffset, false);
            }
        };

        listEl.addEventListener('scroll', handleScroll);
        return () => listEl.removeEventListener('scroll', handleScroll);
    }, [open, hasMoreHistory, loadingHistory, historyOffset, fetchHistory]);

    const postMessage = async (messageObj) => {
        const payload = {
            chatUserId: decryptedAgentCode,
            message: messageObj
        };
        const headers = {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
        };
        const response = await fetch(RAG_API_ENDPOINTS.message, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Message send failed');
        return response.json();
    };

    const handleApiResponse = (data) => {
        const apiMessages = data?.messages || [];
        const isFileUpload = data?.metadata?.isFileUpload || false;
        setShowFileUpload(isFileUpload);
        if (data?.metadata?.ticket_id) setTicketId(data?.metadata?.ticket_id);

        let nextKeyboard = [];
        const botMsgs = [];
        for (const m of apiMessages) {
            if (m?.text) botMsgs.push({ id: `b-${Date.now()}-${Math.random()}`, role: 'bot', text: m.text });
            if (Array.isArray(m?.keyboard) && m.keyboard.length) nextKeyboard = m.keyboard;
        }
        if (botMsgs.length) {
            setMessages((prev) => [...prev, ...botMsgs]);
            setLastAddedMessageId(botMsgs[0]?.id || null);
        }
        setQuickOptions(nextKeyboard);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            setMessages((m) => [...m, { id: `b-${Date.now()}`, role: 'bot', text: 'Please upload only images or PDF files.' }]);
            return;
        }
        if (file.size > 3 * 1024 * 1024) {
            setMessages((m) => [...m, { id: `b-${Date.now()}`, role: 'bot', text: 'File size should be less than 3 MB.' }]);
            return;
        }

        setIsUploading(true);
        try {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = async () => {
                const base64 = reader.result;
                const payload = {
                    filename: file.name,
                    mime: file.type,
                    base64: base64,
                    ticket_id: ticketId
                };
                const headers = {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                };
                const response = await fetch(RAG_API_ENDPOINTS.uploadAttachment, {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify(payload)
                });
                if (!response.ok) throw new Error('Upload failed');
                const uploadData = await response.json();

                // Success message in chat
                setMessages((m) => [...m, { id: `u-${Date.now()}`, role: 'user', text: `Uploaded: ${file.name}` }]);
                setShowFileUpload(false);

                // Call message API with the document payload
                const messageObj = {
                    type: 'document',
                    document: {
                        fileName: uploadData.filename,
                        mimeType: uploadData.mime,
                        base64: uploadData.base64_data
                    }
                };
                const messageData = await postMessage(messageObj);
                handleApiResponse(messageData);
                setIsUploading(false);
            };
        } catch (err) {
            setMessages((m) => [...m, { id: `b-${Date.now()}`, role: 'bot', text: 'Sorry, the file upload failed. Please try again.' }]);
            setIsUploading(false);
        }
    };

    const send = async () => {
        const text = value.trim();
        if (!text) return;
        const userMsg = { id: `u-${Date.now()}`, role: 'user', text };
        setMessages((m) => [...m, userMsg]);
        setValue('');
        setQuickOptions([]);
        try {
            setIsTyping(true);
            const data = await postMessage({ type: 'text', text });
            handleApiResponse(data);
        } catch (err) {
            setMessages((m) => [...m, { id: `b-${Date.now()}`, role: 'bot', text: 'Sorry, I could not send that. Please try again.' }]);
        } finally {
            setIsTyping(false);
        }
    };

    const onQuickOptionClick = async (opt) => {
        const text = opt?.text || '';
        const payload = opt?.payload;
        const userMsg = { id: `u-${Date.now()}`, role: 'user', text: text || payload || '...' };
        setMessages((m) => [...m, userMsg]);
        setQuickOptions([]);
        setShowFileUpload(false);
        try {
            setIsTyping(true);
            const messageObj = payload ? { type: 'button', payload, text } : { type: 'text', text: text || '' };
            const data = await postMessage(messageObj);
            handleApiResponse(data);
        } catch (err) {
            setMessages((m) => [...m, { id: `b-${Date.now()}`, role: 'bot', text: 'Sorry, I could not send that. Please try again.' }]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const placeholder = useMemo(() => 'Ask about services, limits, settlements, onboarding, or anything…', []);

    return (
        <div className={`ace-chatbot ${open ? 'open' : ''}`}>
            <button className="ace-fab" onClick={toggle} aria-label="Open chat">
                <span className="ace-fab-glow" />
                <span className="ace-fab-core">
                    {open ? (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    ) : (
                        <>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M12 3C7.03 3 3 6.58 3 11c0 2.31 1.13 4.39 2.96 5.86-.08.76-.36 1.86-1.18 3.01 0 0 1.6-.09 3.1-1.03.68.19 1.4.29 2.12.29 4.97 0 9-3.58 9-8s-4.03-8-9-8Z"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                />
                            </svg>
                            <span className="ace-fab-badge">AI</span>
                        </>
                    )}
                </span>
                {!open && (
                    <span className="ace-fab-orbit" aria-hidden>
                        <span className="p" />
                        <span className="p" />
                        <span className="p" />
                    </span>
                )}
            </button>

            <div className="ace-panel">
                <div className="ace-header">
                    <div className="ace-title">
                        <span className="ace-dot" /> ACE GURU
                    </div>
                    <div className="ace-status">Online</div>
                </div>
                <div className="ace-body" ref={listRef}>
                    {loadingHistory && (
                        <div className="ace-msg bot" data-mid="loading-history">
                            <div className="bubble typing">
                                <span className="dot" />
                                <span className="dot" />
                                <span className="dot" />
                            </div>
                        </div>
                    )}
                    {messages.map((m) => (
                        <div key={m.id} className={`ace-msg ${m.role}`} data-mid={m.id}>
                            <div className="bubble">
                                {m.role === 'bot' ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown> : m.text}
                                <span className="shine" />
                            </div>
                        </div>
                    ))}
                    {isTyping ? (
                        <div className="ace-msg bot" data-mid="typing">
                            <div className="bubble typing">
                                <span className="dot" />
                                <span className="dot" />
                                <span className="dot" />
                            </div>
                        </div>
                    ) : null}
                </div>
                {quickOptions && (quickOptions.length > 0 || showFileUpload) ? (
                    <div className="ace-quick">
                        {showFileUpload && (
                            <button
                                className="ace-quick-btn upload-btn"
                                onClick={() => document.getElementById('ace-file-upload').click()}
                                disabled={isUploading}
                            >
                                {isUploading ? 'Uploading...' : '📁 Upload File/Image'}
                                <input
                                    id="ace-file-upload"
                                    type="file"
                                    hidden
                                    accept="image/*, application/pdf"
                                    onChange={handleFileChange}
                                />
                            </button>
                        )}
                        {quickOptions.map((opt, idx) => (
                            <button key={`opt-${idx}`} className="ace-quick-btn" onClick={() => onQuickOptionClick(opt)}>
                                {opt?.text || 'Option'}
                            </button>
                        ))}
                    </div>
                ) : null}
                <div className="ace-input">
                    <textarea
                        ref={inputRef}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={handleKey}
                        placeholder={placeholder}
                        rows={3}
                    />
                    <button className="send" onClick={send} aria-label="Send">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M3 11.5l16.5-7-7 16.5-2.5-6-7-3.5Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="currentColor"
                                fillOpacity="0.2"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatWidget;
